import { formatDisplayDate, formatDisplayDateRange } from './displayDate';
export function parseDateSafely(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1;
    const day = parseInt(dateMatch[3], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
}

/**
 * Parse time string (e.g. "5:00 PM") to { hours, minutes }.
 * Used for duration = delivery datetime → strike datetime (24-hour periods).
 */
export function parseTimeToHoursMinutes(timeStr) {
  if (!timeStr || String(timeStr).trim() === '') return null;
  const str = String(timeStr).trim();
  const upper = str.toUpperCase();
  const isPM = upper.indexOf('PM') >= 0;
  const isAM = upper.indexOf('AM') >= 0;
  const numPart = str.replace(/\s*[AP]M\s*$/i, '').trim();
  const colon = numPart.indexOf(':');
  if (colon < 0) return null;
  let hours = parseInt(numPart.substring(0, colon), 10);
  const minutes = parseInt(numPart.substring(colon + 1), 10) || 0;
  if (isNaN(hours)) return null;
  if (isPM && hours !== 12) hours += 12;
  if (isAM && hours === 12) hours = 0;
  hours = Math.max(0, Math.min(23, hours));
  return { hours, minutes: Math.max(0, Math.min(59, minutes)) };
}

/**
 * Build a Date from dateStr (YYYY-MM-DD or parseable) and timeStr (e.g. "5:00 PM").
 * Used to compute duration as delivery datetime → strike datetime (24-hour period).
 */
export function parseDateTimeFromDateAndTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const d = parseDateSafely(dateStr);
  if (!d || isNaN(d.getTime())) return null;
  const tm = parseTimeToHoursMinutes(timeStr);
  if (!tm) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), tm.hours, tm.minutes, 0);
}

/**
 * Get duration in days for rental multiplier (matches Backend/Apps Script).
 * - If deliveryTime + strikeTime + startDate + endDate: duration = ceil(diffHours/24) (24-hour periods).
 * - If only startDate + endDate: duration = floor(diffMs/(24*60*60*1000)) (no +1).
 */
export function getDurationInDays(proposal) {
  const deliveryDateTime = parseDateTimeFromDateAndTime(proposal.startDate, proposal.deliveryTime);
  const strikeDateTime = parseDateTimeFromDateAndTime(proposal.endDate, proposal.strikeTime);
  if (deliveryDateTime && strikeDateTime && !isNaN(deliveryDateTime.getTime()) && !isNaN(strikeDateTime.getTime())) {
    const diffHours = (strikeDateTime.getTime() - deliveryDateTime.getTime()) / (1000 * 60 * 60);
    return Math.max(1, Math.ceil(diffHours / 24));
  }
  const start = parseDateSafely(proposal.startDate);
  const end = parseDateSafely(proposal.endDate);
  if (!start || !end) return 1;
  const diffTime = end.getTime() - start.getTime();
  // Inclusive calendar days when only dates (no times): Jan 1–Jan 3 = 3 days
  return Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
}

// Shared utility function to get a sortable date from a proposal
// Returns a Date object that can be used for sorting (most recent first)
export function getSortableDateFromProposal(proposal) {
  // For historical projects, try to parse eventDate first (it has the actual event date)
  // Only use timestamp if eventDate can't be parsed
  if (proposal.isHistorical) {
    // Try eventDate first for historical projects (it contains the actual event date)
    if (proposal.eventDate) {
      if (proposal.eventDate instanceof Date) {
        if (!isNaN(proposal.eventDate.getTime())) {
          return proposal.eventDate;
        }
      } else if (typeof proposal.eventDate === 'string') {
        const eventDateStr = proposal.eventDate.trim();
        
        // Try parsing GMT date strings first (e.g., "Tue Aug 19 2025 00:00:00 GMT-0500")
        // Handle strings like "Tue Aug 19 2025 00:00:00 GMT-0500 (Central Daylight Time)"
        // Remove timezone name in parentheses if present, as it can cause parsing issues
        let cleanDateStr = eventDateStr;
        // Remove timezone name in parentheses: "(Central Daylight Time)" or "(Central Standard Time)"
        cleanDateStr = cleanDateStr.replace(/\s*\([^)]+\)\s*$/, '');
        
        const dateObj = new Date(cleanDateStr);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          // Only use if it's a reasonable date (not year 1970 from failed parse)
          if (year >= 2000 && year <= 2100) {
            return dateObj;
          }
        }
        
        // Try parsing formatted strings like "April 5 - 13, 2025" or "April 5, 2025"
        const monthMap = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        // Pattern 1: "Month Day - Day, Year" or "Month Day-Day, Year" (date range)
        // Also handles "March 29 - April 3, 2026" (cross-month ranges)
        let match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\d+),?\s+(\d{4})/i);
        if (!match) {
          // Try cross-month: "March 29 - April 3, 2026"
          match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\w+)\s+(\d+),?\s+(\d{4})/i);
          if (match) {
            const startMonthName = match[1].toLowerCase();
            const startMonth = monthMap[startMonthName];
            const startDay = parseInt(match[2]);
            const year = parseInt(match[5]);
            if (startMonth !== undefined && startDay && year) {
              const parsed = new Date(year, startMonth, startDay);
              if (!isNaN(parsed.getTime())) {
                return parsed;
              }
            }
          }
        } else {
          const monthName = match[1].toLowerCase();
          const month = monthMap[monthName];
          const day = parseInt(match[2]); // Use first date for sorting
          const year = parseInt(match[4]);
          if (month !== undefined && day && year) {
            const parsed = new Date(year, month, day);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
        
        // Pattern 2: "Month Day, Year" (single date)
        match = eventDateStr.match(/(\w+)\s+(\d+),?\s+(\d{4})/i);
        if (match) {
          const monthName = match[1].toLowerCase();
          const month = monthMap[monthName];
          const day = parseInt(match[2]);
          const year = parseInt(match[3]);
          if (month !== undefined && day && year) {
            const parsed = new Date(year, month, day);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
      }
    }
    
    // Fallback to timestamp for historical projects only if eventDate parsing failed
    if (proposal.timestamp) {
      const ts = new Date(proposal.timestamp);
      if (!isNaN(ts.getTime())) {
        return ts;
      }
    }
  }
  
  // Debug: Log if we can't find a date
  if (!proposal.startDate && !proposal.eventDate && !proposal.endDate) {
    console.warn('Proposal has no date fields:', proposal.projectNumber || 'unknown', proposal);
  }
  
  // Priority 2: Use startDate if available
  if (proposal.startDate) {
    if (proposal.startDate instanceof Date) {
      if (!isNaN(proposal.startDate.getTime())) {
        return proposal.startDate;
      }
    } else if (typeof proposal.startDate === 'string') {
      // Try YYYY-MM-DD format first
      if (/^\d{4}-\d{2}-\d{2}/.test(proposal.startDate.trim())) {
        const parsed = parseDateSafely(proposal.startDate);
        if (parsed && !isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      // Try parsing as-is
      const parsed = new Date(proposal.startDate);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  
  // Priority 3: Try to parse eventDate (can be in many formats)
  if (proposal.eventDate) {
    if (proposal.eventDate instanceof Date) {
      if (!isNaN(proposal.eventDate.getTime())) {
        return proposal.eventDate;
      }
    } else if (typeof proposal.eventDate === 'string') {
      const eventDateStr = proposal.eventDate.trim();
      
      // Try parsing as Date object first (handles GMT strings and ISO dates)
      const dateObj = new Date(eventDateStr);
      if (!isNaN(dateObj.getTime())) {
        // Only use this if it's a reasonable date (not year 1970 or 1900 from failed parse)
        const year = dateObj.getFullYear();
        if (year >= 2000 && year <= 2100) {
          return dateObj;
        }
      }
      
      // Try parsing formatted strings like "April 5 - 13, 2025" or "April 5, 2025"
      const monthMap = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
      };
      
      // Pattern 1: "Month Day - Day, Year" or "Month Day-Day, Year" (date range)
      // Also handles "March 29 - April 3, 2026" (cross-month ranges)
      let match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\d+),?\s+(\d{4})/i);
      if (!match) {
        // Try cross-month: "March 29 - April 3, 2026"
        match = eventDateStr.match(/(\w+)\s+(\d+)\s*-\s*(\w+)\s+(\d+),?\s+(\d{4})/i);
        if (match) {
          const startMonthName = match[1].toLowerCase();
          const startMonth = monthMap[startMonthName];
          const startDay = parseInt(match[2]);
          const year = parseInt(match[5]);
          if (startMonth !== undefined && startDay && year) {
            const parsed = new Date(year, startMonth, startDay);
            if (!isNaN(parsed.getTime())) {
              return parsed;
            }
          }
        }
      } else {
        const monthName = match[1].toLowerCase();
        const month = monthMap[monthName];
        const day = parseInt(match[2]); // Use first date for sorting
        const year = parseInt(match[4]);
        if (month !== undefined && day && year) {
          const parsed = new Date(year, month, day);
          if (!isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      
      // Pattern 2: "Month Day, Year" (single date)
      match = eventDateStr.match(/(\w+)\s+(\d+),?\s+(\d{4})/i);
      if (match) {
        const monthName = match[1].toLowerCase();
        const month = monthMap[monthName];
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        if (month !== undefined && day && year) {
          const parsed = new Date(year, month, day);
          if (!isNaN(parsed.getTime())) {
            return parsed;
          }
        }
      }
      
      // Pattern 3: MM/DD/YYYY format
      match = eventDateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (match) {
        const month = parseInt(match[1]) - 1;
        const day = parseInt(match[2]);
        const year = parseInt(match[3]);
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      
      // Pattern 4: YYYY-MM-DD format
      match = eventDateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1]);
        const month = parseInt(match[2]) - 1;
        const day = parseInt(match[3]);
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
  }
  
  // Priority 4: Try endDate as last resort
  if (proposal.endDate) {
    if (proposal.endDate instanceof Date && !isNaN(proposal.endDate.getTime())) {
      return proposal.endDate;
    } else if (typeof proposal.endDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(proposal.endDate.trim())) {
      const parsed = parseDateSafely(proposal.endDate);
      if (parsed && !isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  
  // If we can't parse anything, return a very old date so it sorts to the bottom
  return new Date(0);
}

// Sort proposals by date (most recent first)
export function sortProposalsByDate(proposals) {
  return [...proposals].sort((a, b) => {
    const dateA = getSortableDateFromProposal(a);
    const dateB = getSortableDateFromProposal(b);
    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    
    // Primary sort: by date (descending - newest first)
    if (timeB !== timeA) {
      return timeB - timeA;
    }
    
    // Secondary sort: by projectNumber (descending) if dates are equal
    const projectNumA = parseInt(a.projectNumber) || 0;
    const projectNumB = parseInt(b.projectNumber) || 0;
    return projectNumB - projectNumA;
  });
}

export function formatDateRange(proposal) {
  // First priority: Use eventDate if available (it's already formatted from the backend)
  // This works for both historical and regular projects that have eventDate set
  // Check eventDate first - this should be set for all projects (historical and regular)
  if (proposal.eventDate) {
    let eventDateStr = '';
    
    // Handle Date objects - convert to formatted string
    if (proposal.eventDate instanceof Date) {
      // If it's a Date object, format it properly
      eventDateStr = formatDisplayDate(proposal.eventDate);
    } else if (typeof proposal.eventDate === 'string') {
      // Check if it's a Date string (like "Wed Mar 19 2025 00:00:00 GMT-0500")
      // If it looks like a Date string, try to parse and format it
      if (proposal.eventDate.includes('GMT') || proposal.eventDate.match(/^\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/)) {
        const parsedDate = new Date(proposal.eventDate);
        if (!isNaN(parsedDate.getTime())) {
          eventDateStr = formatDisplayDate(parsedDate);
        } else {
          eventDateStr = proposal.eventDate.trim();
        }
      } else {
        // It's already a formatted string
        eventDateStr = proposal.eventDate.trim();
      }
    }
    
    if (eventDateStr) {
      // Normalize spacing around hyphens to be consistent (spaces around dash)
      // This handles both "April 5-13, 2025" and "April 5 - 13, 2025"
      return eventDateStr.replace(/\s*-\s*/g, ' - ');
    }
  }
  
  // Second priority: Try to use startDate and endDate if available
  // Only parse if they exist and are valid date strings (YYYY-MM-DD format)
  let start = null;
  let end = null;
  
  if (proposal.startDate && typeof proposal.startDate === 'string' && proposal.startDate.trim()) {
    // Only parse if it looks like a date string (YYYY-MM-DD), not a formatted string
    const startDateStr = proposal.startDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(startDateStr)) {
      start = parseDateSafely(startDateStr);
      if (start && isNaN(start.getTime())) {
        start = null; // Invalid date
      }
    }
  }
  
  if (proposal.endDate && typeof proposal.endDate === 'string' && proposal.endDate.trim()) {
    // Only parse if it looks like a date string (YYYY-MM-DD), not a formatted string
    const endDateStr = proposal.endDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(endDateStr)) {
      end = parseDateSafely(endDateStr);
      if (end && isNaN(end.getTime())) {
        end = null; // Invalid date
      }
    }
  }
  
  // If we have a start date but no end date, format as single date
  if (start && !end) {
    return formatDisplayDate(start);
  }
  
  // If we have both dates, format showing all dates
  if (start && end) {
    return formatAllDates(start, end);
  }
  
  // If we have nothing, return empty string (will show as 'N/A' in the table)
  return '';
}

// Helper function to format date ranges in the correct format
// Examples: "March 1, 2026" / "March 1-3, 2026" / "March 29 - April 3, 2026"
export function formatAllDates(start, end) {
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '';
  }
  return formatDisplayDateRange(start, end);
}
